import React from "react";
import { flushSync } from "react-dom";
import { constants } from "@qgiv/core-js";
import { trackApplePay, ApplePayPaymentMethod } from "@qgiv/core-donor";
import { useSelector } from "react-redux";
import { useFormikContext } from "formik";
import { getInitialValues } from "./formik";
import { selectShouldDisplayApplePay } from "../../../redux/slices/appSettingsSlice";
import {
    selectAllRegistration,
    selectRegistrationTotals,
} from "../../../redux/slices/registrationSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectAllProducts } from "../../../redux/slices/productsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";

const ConnectedApplePayPaymentMethod = () => {
    const {
        ENUMS: { PaymentType },
    } = constants;
    const {
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        values,
        validateForm,
    } = useFormikContext();
    const isCms = useSelector(selectIsCms);
    const shouldDisplayApplePay = useSelector(selectShouldDisplayApplePay);
    const registration = useSelector(selectAllRegistration);
    const formSettings = useSelector(selectAllFormSettings);
    const { template, VisaAmexDisc } = formSettings;
    const p2pSettings = useSelector(selectAllP2pSettings);
    const products = useSelector(selectAllProducts);
    const { Donation_Amount, Payment_Type } = values;
    const { payment = {} } = registration;
    const {
        giftAssistAmount = 0,
        prePromoRegistrantTotal = 0,
        prePromoTransTotal = 0,
        storeSubtotalAmount = 0,
        total = 0,
    } = useSelector(selectRegistrationTotals);

    const settings = {
        formSettings,
        p2pSettings,
        products,
    };

    // The registration total stored in state before any optional donations, gift assist
    // contributions, store items or discounts modify the value
    const eventRegistrationAmount = prePromoRegistrantTotal;

    // For displaying the total value of all of the discounts
    const discountAmount = prePromoTransTotal - total;

    const savedPaymentValues = payment;

    // Use existing submission infrastructure to handle and display errors that
    // are thrown because of validation failing prior to the display of the
    // Apple Pay payment sheet
    const handleErrorsPriorToPaymentSheetDisplay = () => {
        handleSubmit();
    };

    // Form specific part of the payment request setup
    const onBeforeOpen = (
        defaultApplePayPaymentRequest,
        defaultAdditionalApplePayPaymentRequestData,
        formatAsAmount,
        formatAsNumber,
    ) => {
        let applePayPaymentRequest = {};
        // Copy all of the additional payment request data on to a new object
        const additionalApplePayPaymentRequestData = {
            ...defaultAdditionalApplePayPaymentRequestData,
        };

        // Prepare values for formatting and use in conditional logic by
        // ensuring that every values that is being used is a number
        const eventRegistrationAmountAsNumber = formatAsNumber(
            eventRegistrationAmount,
        );

        const purchaseAmountAsNumber = formatAsNumber(storeSubtotalAmount);
        const donationAmountAsNumber = formatAsNumber(Donation_Amount);
        const discountAmountAsNumber = formatAsNumber(discountAmount);
        const feeCoverageAmountAsNumber = formatAsNumber(giftAssistAmount);
        const totalAmountAsNumber = formatAsNumber(total);

        // Line item for campaign registrations
        if (eventRegistrationAmountAsNumber > 0) {
            const formattedEventRegistrationAmount = formatAsAmount(
                eventRegistrationAmountAsNumber,
            );
            const eventRegistrationsLineItem = {
                label: "Campaign Registration(s)",
                amount: formattedEventRegistrationAmount,
            };

            additionalApplePayPaymentRequestData.lineItems.push(
                eventRegistrationsLineItem,
            );
        }

        // Line item for store purchases during registration
        if (purchaseAmountAsNumber > 0) {
            const formattedPurchaseAmount = formatAsAmount(
                purchaseAmountAsNumber,
            );
            const purchasesLineItem = {
                label: "Purchase(s)",
                amount: formattedPurchaseAmount,
            };

            additionalApplePayPaymentRequestData.lineItems.push(
                purchasesLineItem,
            );
        }

        // Line item for optional donation
        if (donationAmountAsNumber > 0) {
            const formattedDonationAmount = formatAsAmount(
                donationAmountAsNumber,
            );
            const donationAmountLineItem = {
                label: "Donation(s)",
                amount: formattedDonationAmount,
            };

            additionalApplePayPaymentRequestData.lineItems.push(
                donationAmountLineItem,
            );
        }

        // Line item for discount from promo codes
        if (discountAmountAsNumber > 0) {
            const formattedDiscountAmount = formatAsAmount(
                discountAmountAsNumber,
            );
            const discountsLineItem = {
                label: "Discount(s)",
                amount: formattedDiscountAmount,
            };

            additionalApplePayPaymentRequestData.lineItems.push(
                discountsLineItem,
            );
        }

        // Line item for gift assist. The gift assist line item is added after
        // the discounts line item because if the gift assist line item is a
        // percentage the gift assist value is based on the value of the
        // transaction after discounts.
        if (feeCoverageAmountAsNumber > 0) {
            const formattedFeeCoverageAmount = formatAsAmount(
                feeCoverageAmountAsNumber,
            );
            const feeCoverageLineItem = {
                label: "Fee Coverage",
                amount: formattedFeeCoverageAmount,
            };

            additionalApplePayPaymentRequestData.lineItems.push(
                feeCoverageLineItem,
            );
        }

        const formattedTotalTotalAmount = formatAsAmount(totalAmountAsNumber);

        // Total amount displayed on payment sheet
        additionalApplePayPaymentRequestData.total = {
            label: "Total",
            amount: formattedTotalTotalAmount,
        };

        // And add all of the additional data to the default payment request
        applePayPaymentRequest = {
            ...defaultApplePayPaymentRequest,
            ...additionalApplePayPaymentRequestData,
        };

        return applePayPaymentRequest;
    };

    // Form specific integration of the values returned by Apple Pay into the
    // values that are associated with the form that is going to be submitted
    const onBeforeSubmission = (
        platformAgnosticApplePayValues,
        billingContact,
    ) => {
        const platformSpecificApplePayValues = {
            ...platformAgnosticApplePayValues,
        };
        const initialValues = getInitialValues(settings, savedPaymentValues);
        const applePayBillingAddress2 = billingContact.addressLines[1];
        let billingAddress2 = initialValues.Billing_Address_2;

        // If Apple returned a billing address 2 overwrite the initial value
        if (applePayBillingAddress2) {
            billingAddress2 = applePayBillingAddress2;
        }

        // Overwrite the billing address 2 value as Apple data takes priority
        platformSpecificApplePayValues.Billing_Address_2 = billingAddress2;

        return platformSpecificApplePayValues;
    };

    // -------------------------------------------------------------------------
    // NOTE:
    // It appears that the change to automatically batch renders as part of the
    // upgrade to React 18 has caused this call to handleSubmit() to run the
    // onSubmit() logic responsible for form submission before the
    // Apple_Pay_Token value has had a chance to update. And this was causing
    // Apple Pay submissions to fail the first time they were attempted.
    // Because of this we are using flushSync() to ensure that when onSubmit()
    // it is running with an updated Apple_Pay_Token value. We are not sure
    // why this is happening in this app but not in the legacy version of the
    // React donation form app or the Event Registration app. Because of that
    // we scoped this  solution to P2P for now. However if these issues pop up
    // there this solution should be extended to all of those applications too.
    // -------------------------------------------------------------------------
    // Use existing submission infrastructure to handle form submission after
    // an Apple Pay transaction has been successfully completed
    const handleSubmissionAfterAuthorization = () => {
        flushSync(() => {
            handleSubmit();
            trackApplePay(template);
        });
    };

    // Do not display Apple Pay processing state if Payment_Type is not Apple Pay
    const isApplePay = Payment_Type === PaymentType.APPLEPAY;
    const isDisabled = false;

    const applePayPaymentMethodProps = {
        handleErrorsPriorToPaymentSheetDisplay,
        handleSubmissionAfterAuthorization,
        isApplePay,
        isCms,
        isDisabled,
        isSubmitting,
        onBeforeOpen,
        onBeforeSubmission,
        setFieldValue,
        setValues,
        shouldDisplayApplePay,
        validateForm,
        values,
        VisaAmexDisc,
    };

    return <ApplePayPaymentMethod {...applePayPaymentMethodProps} />;
};

export default ConnectedApplePayPaymentMethod;
